import { omit } from 'lodash';
import moment from 'moment';
import URI from 'urijs';
import * as yup from 'yup';
import { GM_NUMBER_PATTERN } from 'Components/utils/common-validator/CommonValidators';
import { momentToApiDate } from 'Utils/date/apiDate';
import graphQLClient from 'Utils/graphql-client/graphqlClient';
import { getPhoneRegex } from 'Utils/regex/RegexHelper';
import { CUSTOMER_BOOKING_PREBOOKING_FORM_SUBMIT } from 'Utils/request/requestDictionary';
import { TRACK_EVENT_NAMES } from 'Tracking/constants';
import type { NewsletterOptins } from 'Components/lines/prebooking-form-line/PrebookingFormLine.utils';
import {
  MAX_FORM_ADULTS,
  MAX_FORM_KIDS,
  MIN_FORM_ADULTS,
  MIN_FORM_KIDS,
} from './PrebookingForm.constants';
export type FormFieldOption = {
  name: string;
  value: string;
};
export type FormCivilities = {
  adults: Array<FormFieldOption>;
  kids: Array<FormFieldOption>;
};
export type FormCompanion = {
  birthdate: null | timestamp;
  civility: string;
  firstName: string;
  firstNameUnicode?: string;
  lastName: string;
  lastNameUnicode?: string;
};
export type FormTraveler = {
  birthdate: null | timestamp;
  civility: string;
  email: string;
  firstName: string;
  firstNameUnicode?: string;
  lastName: string;
  lastNameUnicode?: string;
  memberNumber: string;
  phone: string;
};
export type FormValues = {
  accommodation: string;
  accommodationCategory: string;
  adults: Array<FormCompanion>;
  adultsNumber: string;
  agency?: string;
  departureCity: string;
  departureDate: {
    from: null | Date;
    to: null;
  };
  duration: string;
  kids: Array<FormCompanion>;
  kidsNumber: string;
  optinEmail?: boolean;
  optinPhone?: boolean;
  optinSms?: boolean;
  productId: string;
  traveler: FormTraveler;
};
export type InitialFormData = {
  traveler: FormTraveler;
  productId: string;
  optinEmail: boolean;
  optinPhone: boolean;
  optinSms: boolean;
};

// Map Traveler in User Context
const getDateTimestamp = (birthdate): timestamp => {
  return moment(birthdate).valueOf();
};

export const getFormUser = (user: UserInfo): FormTraveler => {
  const {
    civility = '',
    dateOfBirth,
    email = '',
    firstName = '',
    GMNumber = '',
    lastName = '',
    mobilePhoneNumber = '',
    unicodeFirstName,
    unicodeLastName,
  } = user;
  return {
    civility,
    birthdate: dateOfBirth ? getDateTimestamp(dateOfBirth) : null,
    email,
    firstName,
    firstNameUnicode: unicodeFirstName,
    memberNumber: GMNumber,
    lastName,
    lastNameUnicode: unicodeLastName,
    phone: mobilePhoneNumber,
  };
};
export const getFormOptins = (userInfo: UserInfo, consentOptins: NewsletterOptins) => {
  return {
    optinEmail:
      'optinEmail' in userInfo
        ? userInfo.optinEmail || false
        : consentOptins.optinEmail?.preChecked || false,
    optinPhone:
      'optinPhone' in userInfo
        ? userInfo.optinPhone || false
        : consentOptins.optinPhone?.preChecked || false,
    optinSms:
      'optinSms' in userInfo
        ? userInfo.optinSms || false
        : consentOptins.optinSms?.preChecked || false,
  };
};
// Initialize Form
export const getInitialFormValues = (initialFormData: InitialFormData): FormValues => {
  const { traveler, productId, optinEmail, optinPhone, optinSms } = initialFormData;
  return {
    accommodation: '',
    accommodationCategory: '000022',
    adults: [
      {
        birthdate: null,
        civility: '',
        firstName: '',
        lastName: '',
      },
    ],
    adultsNumber: '2',
    agency: '',
    departureCity: 'NO',
    departureDate: {
      from: null,
      to: null,
    },
    duration: '',
    kids: [],
    kidsNumber: MIN_FORM_KIDS.toString(),
    optinEmail: optinEmail,
    optinPhone: optinPhone,
    optinSms: optinSms,
    productId: productId,
    traveler,
  };
};
// Set Form Schema
type FormSchemaParameters = {
  agencies: Array<string>;
  civilities: FormCivilities;
  locale: string;
  productIds: Array<string>;
};
// Birthdate from 1920
const minBirthdateTimestamp = moment('1920-01-01').valueOf();
// Birthdate less than 18 years old
const kidBirthdateTimestamp = moment().subtract(18, 'years').valueOf();
const adultBirthdateSchema = yup
  .number()
  .min(minBirthdateTimestamp)
  .max(kidBirthdateTimestamp)
  .nullable();
export const getAdultBirthdateValidator = (): yup => adultBirthdateSchema;
export const getKidFutureBithdateValidator = (
  minAuthorizedAgeInMonths?: number,
  errorMessage: string = 'error',
): yup =>
  yup
    .number()
    .transform((value) => (Number.isNaN(value) ? undefined : value))
    .test('isOldEnough', errorMessage, function (value: number) {
      const { context } = this.options;

      if (value && context?.values.departureDate.from) {
        const departureDate = moment(context?.values.departureDate.from).startOf('day');
        const kidBirthdate = moment(value).startOf('day');
        const validBirthdate = kidBirthdate.isBefore(departureDate);

        if (minAuthorizedAgeInMonths) {
          const minAuthorizedMoment = departureDate.subtract(minAuthorizedAgeInMonths, 'months');
          return validBirthdate && kidBirthdate.isBefore(minAuthorizedMoment);
        }

        return validBirthdate;
      }

      return false;
    })
    .nullable()
    .required();
export const getFormSchema = (
  t: (arg0: string) => string,
  { agencies, civilities, locale, productIds }: FormSchemaParameters,
  formStep: number,
) => {
  const agencySchema = yup.mixed().oneOf(agencies).required();
  const fistNameSchema = yup
    .string()
    .required(t('prebookingForm.mainTraveler.firstNamePlaceholder'))
    .max(20, t('prebookingForm.mainTraveler.firstNamePlaceholder'));
  const lastNameSchema = yup
    .string()
    .required(t('prebookingForm.mainTraveler.firstNamePlaceholder'))
    .max(30, t('prebookingForm.mainTraveler.firstNamePlaceholder'));
  const adultCivilities = civilities.adults.map((civility) => civility.value);
  const kidCivilities = civilities.kids.map((civility) => civility.value);
  const adultCompanionSchema = yup.object().shape({
    civility: yup.mixed().oneOf(adultCivilities).required(),
    birthdate: adultBirthdateSchema,
    firstName: fistNameSchema,
    lastName: lastNameSchema,
  });
  const kidCompanionSchema = yup.object().shape({
    civility: yup.mixed().oneOf(kidCivilities),
    birthdate: yup.number().min(kidBirthdateTimestamp).nullable().required(),
    firstName: fistNameSchema,
    lastName: lastNameSchema,
  });
  const kidBirthdaySchema = yup.object().shape({
    birthdate: yup
      .number()
      .transform((value) => (Number.isNaN(value) ? undefined : value))
      .min(kidBirthdateTimestamp)
      .nullable()
      .required(t('prebookingForm.pax.childCompanionErrorLabel')),
  });
  const travelerSchema = yup.object().shape({
    birthdate: adultBirthdateSchema,
    civility: yup.mixed().oneOf(adultCivilities).required(),
    email: yup
      .string()
      .email(t('prebookingForm.mainTraveler.emailPlaceholder'))
      .required(t('prebookingForm.mainTraveler.emailPlaceholder')),
    firstName: fistNameSchema,
    lastName: lastNameSchema,
    memberNumber: yup.string().matches(GM_NUMBER_PATTERN),
    phone: yup
      .string()
      .required(t('prebookingForm.mainTraveler.phonePlaceholder'))
      .matches(getPhoneRegex(locale), {
        excludeEmptyString: true,
        message: t('prebookingForm.mainTraveler.phonePlaceholder'),
      }),
  });

  const returnFullSchema = () => {
    return yup.object().shape({
      accommodationCategory: yup.string().required(),
      adults: yup
        .array()
        .min(MIN_FORM_ADULTS - 1)
        .max(MAX_FORM_ADULTS - 1)
        .of(adultCompanionSchema),
      adultsNumber: yup.string().required(),
      ...(agencies.length
        ? {
            agency: agencySchema,
          }
        : null),
      departureCity: yup.string().required(),
      departureDate: yup
        .object()
        .shape({
          from: yup.date(),
          to: yup.date().nullable(),
        })
        .required(),
      duration: yup
        .string()
        .required()
        .matches(/^[0-9]{1,2}$/),
      // 1 or 2 digits
      kids: yup.array().min(MIN_FORM_KIDS).max(MAX_FORM_KIDS).of(kidCompanionSchema),
      kidsNumber: yup.string().required(),
      optinEmail: yup.boolean(),
      optinPhone: yup.boolean(),
      optinSms: yup.boolean(),
      productId: yup.mixed().oneOf(productIds).required(),
      traveler: travelerSchema,
    });
  };

  switch (formStep) {
    case 0:
      return yup.object().shape({
        accommodationCategory: yup.string().required(),
        adultsNumber: yup.string().required(),
        departureCity: yup.string().required(),
        departureDate: yup
          .object()
          .shape({
            from: yup.date(),
            to: yup.date().nullable(),
          })
          .required(),
        duration: yup
          .string()
          .required()
          .matches(/^[0-9]{1,2}$/),
        // 1 or 2 digits
        kids: yup.array().min(MIN_FORM_KIDS).max(MAX_FORM_KIDS).of(kidBirthdaySchema),
        kidsNumber: yup.string().required(),
        productId: yup.mixed().oneOf(productIds).required(),
      });

    default:
      return returnFullSchema();
  }
};
// Submit prebooking form
type FormSubmitParameters = {
  appContext: AppContext;
  redirects: {
    errorUrl: string;
    successUrl: string;
  };
  router: any;
  trackClickEvent: (name: string, payload?: TrackPayload) => void;
  setHasError: (arg0: boolean) => void;
  proposalId?: string;
  proposalPrice?: ProposalPrice;
};

const formatDate = (date: timestamp | Date) => momentToApiDate(moment(date));

const getCompanionQuery = (companion) => {
  const {
    birthdate,
    civility,
    firstName,
    firstNameUnicode = '',
    lastName,
    lastNameUnicode = '',
  } = companion;
  return `{
    ${birthdate ? `birthdate: "${formatDate(birthdate)}"` : ''}
    civility: "${civility}"
    lastName: "${lastName}"
    unicodeLastName: "${lastNameUnicode}"
    firstName: "${firstName}"
    unicodeFirstName: "${firstNameUnicode}"
  }`;
};

const getCompanionsQuery = (companions) => {
  return companions.reduce((acc, companion) => {
    return `${acc}${getCompanionQuery(companion)}`;
  }, '');
};

export const getPrebookingFormQuery = (
  values: FormValues,
  proposalId?: string,
  proposalPrice?: {
    amount: number;
    currency: string;
  },
) => {
  const {
    accommodation,
    accommodationCategory,
    adults,
    agency,
    departureCity,
    departureDate,
    duration,
    kids,
    optinEmail,
    optinPhone,
    optinSms,
    productId,
    traveler,
  } = values;
  const {
    birthdate,
    civility,
    email,
    firstName,
    firstNameUnicode = '',
    lastName,
    lastNameUnicode = '',
    memberNumber = '',
    phone,
  } = traveler;
  const price = proposalPrice
    ? `{ amount: ${proposalPrice.amount}, currency: "${proposalPrice.currency}" }`
    : 'null';
  return `mutation {
    prebooking(
      productId: "${productId}"
      proposalId: ${proposalId ? `"${proposalId}"` : 'null'}
      price: ${price}
      departureCity: "${departureCity}"
      departureDate: "${formatDate(departureDate.from || Date.now())}"
      duration: ${parseInt(duration, 10)}
      accommodation: ${accommodation ? `"${accommodation}"` : 'null'}
      accommodationCategory: "${accommodationCategory}"
      agency: ${agency ? `"${agency}"` : 'null'}
      mainContact: {
        gmNumber: "${memberNumber}"
        civility: "${civility}"
        lastName: "${lastName}"
        unicodeLastName: "${lastNameUnicode}"
        firstName: "${firstName}"
        unicodeFirstName: "${firstNameUnicode}"
        ${birthdate ? `birthdate: "${formatDate(birthdate)}"` : ''}
        email: "${email}"
        mobile: "${phone}"
        ${typeof optinSms !== 'undefined' ? `optinSms: ${optinSms.toString()}` : ''}
        ${typeof optinPhone !== 'undefined' ? `optinPhone: ${optinPhone.toString()}` : ''}
        ${typeof optinEmail !== 'undefined' ? `optinEmail: ${optinEmail.toString()}` : ''}
      }
      adults: [${getCompanionsQuery(adults)}]
      children: [${getCompanionsQuery(kids)}]
    ) {
      errors {
        errorKey
        errorCode
      }
      neolaneID
    }
  }`;
};
type FormResponse = {
  prebooking: {
    neolaneID: null | string;
    errors: Array<{
      errorCode: string;
      errorKey: string;
    }>;
  };
};
export const handleSubmit = async (values: FormValues, params: FormSubmitParameters) => {
  const { appContext, redirects, router, setHasError, proposalId, trackClickEvent, proposalPrice } =
    params;
  const formattedPrice = proposalPrice
    ? {
        amount: proposalPrice.total.value,
        currency: proposalPrice.currency,
      }
    : undefined;
  const { successUrl } = redirects;
  const prebookingFormQuery = getPrebookingFormQuery(values, proposalId, formattedPrice);
  const queryParams = omit(router.query, ['dynamicPage']);
  trackClickEvent(TRACK_EVENT_NAMES.CLICK_PREBOOKING_FORM_PREREG, {
    detail_click: 'submit_my_request',
  });

  try {
    // Submit data
    const { prebooking }: FormResponse = await graphQLClient.runNoCacheQuery(
      prebookingFormQuery,
      appContext,
      {
        requestName: CUSTOMER_BOOKING_PREBOOKING_FORM_SUBMIT,
      },
    );
    // Redirect
    const requestHasSucceeded = prebooking.errors.length === 0;

    if (!requestHasSucceeded) {
      return setHasError(true);
    }

    const redirectUri = successUrl;
    return router.push(
      new URI(redirectUri)
        .query({ ...queryParams, trackingPageLoadAppendSuffix: values.productId, proposalId })
        .toString(),
    );
  } catch (error) {
    trackClickEvent(TRACK_EVENT_NAMES.CLICK_PREBOOKING_FORM_PREREG, {
      detail_click: 'request_unsuccessfull',
    });
    return setHasError(true);
  }
};
